import React, { Children, Component, Fragment, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { lowerCase, pickBy, identity } from 'lodash/fp';

import { snakeCaseObject, isObjectEmpty } from 'lib/utils';
import { validateField, validators } from 'lib/validations';
import { formatPhotoUrl } from 'lib/formatters';

import Button from 'components/button';
import Box from 'components/box';
import DetailSection from 'components/detail-section';
import { MetadataItem, MetadataList } from 'components/list';
import { ViewTitle } from 'components/text';
import { UnderlinedAnchor } from 'components/links';
import Modal from 'components/modal';
import { Alert, MessageAlert } from 'components/alerts';

import EditObjectForm from 'containers/edit-object-form/edit-object-form';

import MemberPhoto from './member-photo';
import MembershipStatusIndicator from './membership-status-indicator';
import MemberCardReplacement from './member-card-replacement';

const emptyFields = {
  fullName: '',
  gender: '',
  profession: '',
  phoneNumber: '',
  photo: '',
};

class MemberDetails extends Component {
  state = {
    currentEditMemberField: null,
    fields: emptyFields,
    errors: emptyFields,
    displayServerError: false,
    showCardReplacementModal: false,
  }

  setCurrentEditMemberField = (field) => {
    this.setState({ currentEditMemberField: field });
  }

  handleEditMemberModalFieldChange = (e) => {
    const { errors, fields } = this.state;
    this.setState({
      errors: { ...errors, [e.target.name]: '' },
      fields: { ...fields, [e.target.name]: e.target.name === 'photo' ? e.target.files : e.target.value },
    });
  }

  clearFields = () => {
    this.setState({
      fields: emptyFields,
      errors: emptyFields,
    });
  }

  closeEditMemberModal = () => {
    this.clearFields();
    this.setState({
      currentEditMemberField: null,
      displayServerError: false,
    });
  }

  checkFieldErrors = (callback) => {
    const { currentEditMemberField, fields, errors } = this.state;

    this.setState({
      errors: {
        ...errors,
        [currentEditMemberField]:
          validateField(validators[currentEditMemberField], fields[currentEditMemberField], null),
      },
    }, callback);
  }

  resolveUpdate = (action) => {
    if (action.errorMessage) {
      this.setState({ displayServerError: true });
    } else {
      this.closeEditMemberModal();
    }
  }

  sendPhotoUpdateRequest = () => {
    const { member, updateMemberPhoto } = this.props;
    const { id } = member;
    const { fields, errors } = this.state;

    if (isObjectEmpty(errors)) {
      updateMemberPhoto(id, fields.photo[0]).then((action) => {
        this.resolveUpdate(action);
      });
    }
  }

  sendFieldUpdateRequest = () => {
    const { member, updateMember } = this.props;
    const { id } = member;
    const { fields, errors } = this.state;

    const memberChanges = pickBy(identity)(fields);
    memberChanges.id = id;

    if (isObjectEmpty(errors)) {
      updateMember(snakeCaseObject(memberChanges)).then((action) => {
        this.resolveUpdate(action);
      });
    }
  }

  handleEditMemberSubmit = (e, field) => {
    e.preventDefault();

    // passing in sending request function as callback
    if (field === 'photo') {
      this.checkFieldErrors(this.sendPhotoUpdateRequest);
    } else {
      this.checkFieldErrors(this.sendFieldUpdateRequest);
    }
  }

  renewMember = () => {
    const { history, member } = this.props;
    history.push(`/members/${member.id}/renew`);    
  }

  hideCardReplacementModal = () => {
    this.setState({ showCardReplacementModal: false });
  }

  handleAcceptCardReplacement = ({ paymentDate, receiptNumber }) => {
    const { handleReplaceCard, cardReplacementFee } = this.props;
    this.setState({ showCardReplacementModal: false });
    handleReplaceCard({ paymentDate, receiptNumber, cardReplacementFee });
  }

  renderAction(field) {
    return (<UnderlinedAnchor onClick={() => this.setCurrentEditMemberField(field)}>Edit</UnderlinedAnchor>);
  }

  render() {
    const {
      member,
      membershipStatus,
      memberCanRenew,
      isSubmitting,
      children,
      updateMemberPhoto,
      primaryAction,
      editableItemNames,
      cardReplacementFee,
    } = this.props;
    const { currentEditMemberField, fields, errors, displayServerError, showCardReplacementModal } = this.state;

    const editModalTitle = lowerCase(currentEditMemberField) === 'address'
      ? 'Relocate to other address'
      : `Edit ${lowerCase(currentEditMemberField)}`;

    return (
      <Fragment>
        { memberCanRenew && (
          <MessageAlert
            type="warning"
            title="Member can renew"
            description="Membership has not yet renewed for the current enrollment period."
          />
        )}
        <Box flex justifyContent="space-between">
          <DetailSection>
            <Box flex justifyContent="flex-start">
              <MemberPhoto src={formatPhotoUrl(member.photoUrl)} name="Name" />
              <Box marginLeft={4}>
                <ViewTitle>{member.fullName}</ViewTitle>
              </Box>
            </Box>
            {updateMemberPhoto && (
              <Box marginTop={3}>
                <UnderlinedAnchor onClick={() => this.setCurrentEditMemberField('photo')}>Edit photo</UnderlinedAnchor>
              </Box>
            )}
          </DetailSection>
          {primaryAction
            && (
              <Box>
                <Button primary onClick={primaryAction.onClick}>{primaryAction.buttonText}</Button>
              </Box>
            )
          }
        </Box>
        {memberCanRenew && <Box marginTop={5} flex justifyContent="flex-end">
          <UnderlinedAnchor onClick={() => this.renewMember()}>Renew</UnderlinedAnchor>
        </Box>}
        <DetailSection title="Membership Status">
          <MetadataList>
            <MetadataItem label="Member" value={<MembershipStatusIndicator statusEnum={membershipStatus.memberStatusEnum} statusDate={membershipStatus.memberStatusDate} />} />
            {membershipStatus.beneficiaryStatusEnum
            && <MetadataItem label="Beneficiary" value={<MembershipStatusIndicator statusEnum={membershipStatus.beneficiaryStatusEnum} statusDate={membershipStatus.beneficiaryStatusDate} />} />
            }
          </MetadataList>
        </DetailSection>
        <DetailSection title="Member information">
          <MetadataList>
            {Children.map(children, (child) => {
              const { name } = child.props;
              if (editableItemNames.includes(name)) {
                return cloneElement(child, { action: this.renderAction(name) });
              }
              return child;
            })}
          </MetadataList>
        </DetailSection>
        {currentEditMemberField && (
          <Modal title={editModalTitle} onRequestClose={this.closeEditMemberModal} maxWidth={700}>
            {displayServerError && (
              <Box marginBottom={4}>
                <Alert>An unknown error occurred. Please refresh the page and try again.</Alert>
              </Box>
            )}
            <EditObjectForm
              isSubmitting={isSubmitting}
              field={currentEditMemberField}
              onCancel={this.closeEditMemberModal}
              onSubmit={this.handleEditMemberSubmit}
              object={fields}
              errors={errors}
              handleFieldChange={this.handleEditMemberModalFieldChange}
            />
          </Modal>
        )}
        <MemberCardReplacement
          fee={cardReplacementFee}
          isModalShown={showCardReplacementModal}
          handleCancel={this.hideCardReplacementModal}
          handleAccept={this.handleAcceptCardReplacement}
        />
      </Fragment>
    );
  }
}

export default MemberDetails;


MemberDetails.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.string,
    photoUrl: PropTypes.string,
    fullName: PropTypes.string,
  }),
  membershipStatus: PropTypes.shape({
    memberStatusEnum: PropTypes.string.isRequired,
    memberStatusDate: PropTypes.string.isRequired,
    beneficiaryStatusEnum: PropTypes.string,
    beneficiaryStatusDate: PropTypes.string,
  }),
  memberCanRenew: PropTypes.bool,
  updateMember: PropTypes.func.isRequired,
  updateMemberPhoto: PropTypes.func,
  isSubmitting: PropTypes.bool.isRequired,
  children: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  editableItemNames: PropTypes.arrayOf(PropTypes.string),
  primaryAction: PropTypes.shape({
    buttonText: PropTypes.string,
    onClick: PropTypes.func,
  }),
  cardReplacementFee: PropTypes.shape({
    amount: PropTypes.number.isRequired,
  }),
  handleReplaceCard: PropTypes.func.isRequired,
};

MemberDetails.defaultProps = {
  editableItemNames: [],
  member: {},
  membershipStatus: {},
  memberCanRenew: null,
  updateMemberPhoto: null,
  primaryAction: null,
  cardReplacementFee: {
    amount: 0,
  },
};
